import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Package,
  Plus,
  Server,
  CheckCircle2,
  XCircle,
  AlertCircle,
  CreditCard,
  Clock,
} from "lucide-react";
import { TrialBanner } from "@/components/TrialBanner";

interface Instance {
  id: string;
  name: string;
  odoo_url: string;
  status: "active" | "inactive" | "error";
  health_status: "healthy" | "degraded" | "down";
  active_bobs: number;
  tier: string;
  created_at: string;
}

/**
 * SoloDashboard - Dashboard for direct solo customers
 *
 * Key differences from ClientDashboard:
 * - Shows trial status banner when applicable
 * - Links to solo-specific billing (prepaid only)
 * - Simplified navigation
 * - No partner context
 */
export function SoloDashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Connect to real API
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-instances"],
    queryFn: async () => {
      try {
        return await api.getMyInstances();
      } catch (err) {
        console.error("Error fetching instances:", err);
        throw err;
      }
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-300">Loading your instances...</div>
      </div>
    );
  }

  if (error) {
    const errorMessage =
      (error as { message?: string })?.message || "Unknown error";
    const errorStatus = (error as { status?: number })?.status;

    return (
      <div className="flex flex-col items-center justify-center h-96 gap-4">
        <div className="text-red-400">Error loading instances</div>
        <div className="text-sm text-slate-500">
          {errorStatus === 401
            ? "Please log in to view your instances"
            : errorStatus === 403
              ? "You do not have permission to view instances"
              : errorMessage}
        </div>
        {errorStatus === 401 && (
          <button
            onClick={() => navigate("/login")}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
          >
            Go to Login
          </button>
        )}
      </div>
    );
  }

  const instances = data?.instances || [];

  const getHealthIcon = (status: Instance["health_status"]) => {
    switch (status) {
      case "healthy":
        return <CheckCircle2 className="h-5 w-5 text-emerald-400" aria-hidden="true" />;
      case "degraded":
        return <AlertCircle className="h-5 w-5 text-yellow-400" aria-hidden="true" />;
      case "down":
        return <XCircle className="h-5 w-5 text-red-400" aria-hidden="true" />;
    }
  };

  const getHealthLabel = (status: Instance["health_status"]) => {
    switch (status) {
      case "healthy":
        return "Healthy";
      case "degraded":
        return "Degraded";
      case "down":
        return "Down";
    }
  };

  const getHealthColor = (status: Instance["health_status"]) => {
    switch (status) {
      case "healthy":
        return "text-emerald-400";
      case "degraded":
        return "text-yellow-400";
      case "down":
        return "text-red-400";
    }
  };

  return (
    <div className="space-y-6">
      {/* Trial Banner - shown for users on trial */}
      <TrialBanner />

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-100">
            Welcome back{user?.name ? `, ${user.name.split(' ')[0]}` : ''}
          </h1>
          <p className="mt-1 text-sm text-slate-300">
            Manage your Odoo instances and BoBs
          </p>
        </div>
        <button
          onClick={() => navigate("/solo/instances/create")}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors"
          aria-label="Create new instance"
        >
          <Plus className="h-5 w-5" aria-hidden="true" />
          Create New Instance
        </button>
      </div>

      {/* Quick Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300">Total Instances</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">
                {instances.length}
              </p>
            </div>
            <Server className="h-12 w-12 text-blue-400/20" aria-hidden="true" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300">Active BoBs</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">
                {instances.reduce((sum, inst) => sum + inst.active_bobs, 0)}
              </p>
            </div>
            <Package className="h-12 w-12 text-emerald-400/20" aria-hidden="true" />
          </div>
        </div>

        <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300">Healthy Instances</p>
              <p className="text-3xl font-bold text-slate-100 mt-1">
                {instances.filter((inst) => inst.health_status === "healthy").length}
              </p>
            </div>
            <CheckCircle2 className="h-12 w-12 text-emerald-400/20" aria-hidden="true" />
          </div>
        </div>

        <button
          onClick={() => navigate("/solo/billing")}
          className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6 hover:border-blue-500/50 transition-colors text-left"
          aria-label="View billing and subscription"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-300">Billing</p>
              <p className="text-lg font-semibold text-blue-400 mt-1">
                Manage Plan
              </p>
            </div>
            <CreditCard className="h-12 w-12 text-blue-400/20" aria-hidden="true" />
          </div>
        </button>
      </div>

      {/* Empty State */}
      {instances.length === 0 && (
        <div className="space-y-6">
          {/* Hero Empty State */}
          <div className="text-center py-12 px-4 bg-slate-800/50 rounded-lg border border-slate-700">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-500/10 mb-4">
              <Server className="h-8 w-8 text-blue-400" aria-hidden="true" />
            </div>
            <h2 className="text-2xl font-semibold text-slate-100 mb-2">
              Welcome to AIIA
            </h2>
            <p className="text-slate-300 max-w-md mx-auto mb-6">
              You haven't created any Odoo instances yet. Get started by creating your first instance and unlock the power of Business-oriented Bots.
            </p>
            <button
              onClick={() => navigate("/solo/instances/create")}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors shadow-lg"
              aria-label="Create your first Odoo instance"
            >
              <Plus className="h-5 w-5" aria-hidden="true" />
              Create Your First Instance
            </button>
          </div>

          {/* Next Steps Guide */}
          <div>
            <h3 className="text-lg font-semibold text-slate-100 mb-4">
              Getting Started - 3 Simple Steps
            </h3>
            <div className="grid gap-4 md:grid-cols-3">
              {/* Step 1 */}
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-blue-500/10 text-blue-400 font-semibold mb-4">
                  1
                </div>
                <h4 className="font-semibold text-slate-100 mb-2">Create Instance</h4>
                <p className="text-sm text-slate-300">
                  Set up a new Odoo instance by providing your connection details and selecting your plan.
                </p>
              </div>

              {/* Step 2 */}
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-emerald-500/10 text-emerald-400 font-semibold mb-4">
                  2
                </div>
                <h4 className="font-semibold text-slate-100 mb-2">Install Connector</h4>
                <p className="text-sm text-slate-300">
                  Download and install the AIIA Connector plugin to your Odoo instance using our automated installer.
                </p>
              </div>

              {/* Step 3 */}
              <div className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-purple-500/10 text-purple-400 font-semibold mb-4">
                  3
                </div>
                <h4 className="font-semibold text-slate-100 mb-2">Browse BoBs</h4>
                <p className="text-sm text-slate-300">
                  Explore our marketplace and install Business-oriented Bots to automate your workflows.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Instance Grid */}
      {instances.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {instances.map((instance) => (
            <div
              key={instance.id}
              className="bg-slate-800/50 backdrop-blur-sm rounded-lg border border-slate-700 p-6 hover:border-blue-500/50 transition-colors cursor-pointer"
              onClick={() => navigate(`/solo/instances/${instance.id}`)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/solo/instances/${instance.id}`);
                }
              }}
              aria-label={`Manage instance ${instance.name}`}
            >
              {/* Instance Header */}
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-blue-400" aria-hidden="true" />
                    <h3 className="text-lg font-semibold text-slate-100 truncate">
                      {instance.name}
                    </h3>
                  </div>
                  <p className="mt-1 text-sm text-slate-300 truncate">
                    {instance.odoo_url}
                  </p>
                </div>
                <div>{getHealthIcon(instance.health_status)}</div>
              </div>

              {/* Instance Stats */}
              <div className="mt-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Status</span>
                  <span className={getHealthColor(instance.health_status)}>
                    {getHealthLabel(instance.health_status)}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Active BoBs</span>
                  <span className="text-slate-300 font-medium">
                    {instance.active_bobs}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Plan</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 capitalize">
                    {instance.tier}
                  </span>
                </div>
              </div>

              {/* Footer */}
              <div className="mt-4 pt-4 border-t border-slate-700">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/solo/instances/${instance.id}`);
                  }}
                  className="text-sm text-blue-400 hover:text-blue-300 font-medium transition-colors"
                >
                  Manage Instance →
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
